<template>
    <div class="section-balance">

        <div class="d-flex flex-column gap-md">
            <div class="d-flex flex-row align-items-center justify-content-between">
                <span class="text-muted font-size-sm font-weight-500 text-uppercase letter-spacing-sm">{{ t('balance-current_balance') }}</span>
                
                <div class="d-flex align-items-center gap-sm">
                    <KvSkeleton v-if="loading" width="130px" height="40px" style="border-radius: 8px;" />
                    <KvTooltip v-else :text="t('balance-view_recharge_history')">
                        <span 
                            class="balance-clickable display-4 mb-0" 
                            :class="consumptionStatusClass"
                            @click="$emit('open-recharge-history')"
                        >
                            <KvAmount v-if="balance !== null" :value="balance" />
                            <template v-else>—</template>
                        </span>
                    </KvTooltip>

                    <KvTooltip :text="t('balance-manage_credits')">
                        <KvPopup placement="bottom right">
                            <template #trigger="{ toggle }">
                                <KvButton variant="ghost" :disabled="loading" @click="toggle" prepend-icon="ui-settings-02" size="sm" class="btn-manage" />
                            </template>
                            <template #content="{ toggle }">
                                <span class="kv-popup-title">{{ t('balance-credit_options') }}</span>
                                <div class="kv-popup-list">
                                    <KvButton alignment="left" prepend-icon="ui-calendar" variant="outlined" @click="$emit('open-adjust-monthly'); toggle()">
                                        {{ t('balance-adjust_monthly_package') }}
                                    </KvButton>
                                    <KvButton alignment="left" prepend-icon="ui-plus" variant="outlined" @click="$emit('open-extra-package'); toggle()">
                                        {{ t('balance-extra_package') }}
                                    </KvButton>
                                </div>
                            </template>
                        </KvPopup>
                    </KvTooltip>
                </div>
            </div>

            <div class="d-flex flex-column status-container w-100">
                <div class="usage-estimate-container mb-4">
                    <KvSkeleton v-if="loading" width="100%" height="24px" style="border-radius: 6px;" />
                    <div v-else class="usage-estimate">
                        {{ t('balance-consumption_estimated_end_month').replace('{amount}', estimatedUsageEndOfMonth) }}
                    </div>
                </div>

                <div v-if="loading" class="w-100 mb-3">
                    <div class="d-flex justify-content-between mb-2">
                        <KvSkeleton width="100px" height="14px" />
                        <KvSkeleton width="70px" height="14px" />
                    </div>
                    <KvSkeleton width="100%" height="10px" style="border-radius: 6px;" />
                    <KvSkeleton class="mt-2" width="120px" height="12px" />
                </div>

                <template v-else-if="!currentActivePackage && balance <= 0">
                    <div class="alert alert-danger mb-2 py-2 w-100 text-center font-weight-medium credits-alert">
                        {{ t('balance-no_credits_available') }}
                    </div>
                </template>
                
                <template v-else-if="currentActivePackage">
                    <div class="w-100 progress-hover-container" @mouseenter="showFuturePackages = true" @mouseleave="showFuturePackages = false">
                        <div class="d-flex justify-content-between align-items-end mb-2">
                            <span class="font-weight-500 package-label">{{ currentActivePackage.label }}</span>
                            <div class="usage-fraction">
                                <span class="font-weight-500 text-dark">{{ formatNumber(currentActivePackage.used) }}</span>
                                <span class="text-muted mx-1">/</span>
                                <span class="text-muted">{{ formatNumber(currentActivePackage.amount) }}</span>
                            </div>
                        </div>

                        <div class="progress-wrapper mb-3">
                            <div class="progress progress-main">
                                <kv-tooltip 
                                    :text="`${formatPercent(100 - currentActivePackage.percentage)}% ${t('balance-consumed')} (${t('balance-used')}: ${formatNumber(currentActivePackage.used)})`" 
                                    :style="{ width: (100 - currentActivePackage.percentage) + '%' }"
                                    class="h-100"
                                >
                                    <div 
                                        class="progress-bar progress-bar-interactive w-100 h-100"
                                        :class="consumptionStatusClass"
                                    >
                                    </div>
                                </kv-tooltip>
                            </div>
                        </div>

                        <div class="text-muted package-expiry mt-1" v-if="currentActivePackage.available > 0">
                            {{ t('balance-expires_in') }} <human-date :date="currentActivePackage.expiresAt" format="[d]/[M]/[Y]" />
                        </div>

                        <transition name="fade">
                            <div v-if="showFuturePackages && futureActivePackages.length > 0" class="future-packages-dropdown">
                                <div class="future-dropdown-header">
                                    {{ t('balance-next_packages_available') }}
                                </div>
                                <div class="future-packages-scrollable">
                                    <div v-for="(pkg, idx) in futureActivePackages" :key="idx" class="future-pkg-item">
                                        <div class="d-flex justify-content-between mb-1">
                                            <span class="future-pkg-label">{{ pkg.label }}</span>
                                            <span v-if="pkg.available > 0" class="future-pkg-expires">{{ t('balance-expires_at_small') }} <human-date format="[d]/[M]/[Y]" :date="pkg.expiresAt" /></span>
                                        </div>
                                        <div class="progress progress-compact">
                                            <kv-tooltip 
                                                :text="`${formatPercent(100 - pkg.percentage)}% ${t('balance-consumed')} (${formatNumber(pkg.used)})`" 
                                                :style="{ width: (100 - pkg.percentage) + '%' }"
                                                class="h-100"
                                            >
                                                <div 
                                                    class="progress-bar progress-bar-interactive w-100 h-100"
                                                    :class="getPackageStatusClass(pkg)"
                                                ></div>
                                            </kv-tooltip>
                                        </div>
                                        <div class="future-pkg-details mt-1">
                                            {{ formatPercent(100 - pkg.percentage) }}% {{ t('balance-consumed') }} ({{ formatNumber(pkg.used) }} / {{ formatNumber(pkg.amount) }})
                                        </div>
                                    </div>
                                </div>
                                <div class="future-dropdown-footer">
                                    <button class="btn btn-link shadow-none p-0 border-0" @click="$emit('open-recharge-history'); showFuturePackages = false">
                                        {{ t('balance-view_full_history') }} <i class="fa fa-angle-right ml-1"></i>
                                    </button>
                                </div>
                            </div>
                        </transition>
                    </div>
                </template>

                <template v-else>
                    <div class="w-100 p-3 border rounded-lg bg-light text-center">
                        <span class="text-muted empty-state-text">{{ t('balance-no_active_package') }}</span>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import keviApi from "../../keviApi";
import KvPopup from "../../components/ui/kv-popup";
import KvButton from "../../components/form/kv-button.vue";
import ModalRechargeHistory from "./modal-recharge-history.vue";
import ModalAdjustMonthly from "./modal-adjust-monthly.vue";
import ModalExtraPackage from "./modal-extra-package.vue";
import HumanDate from "../../components/human-date/human-date.vue";
import KvSkeleton from "../../components/ui/kv-skeleton.vue";
import KvAmount from "../../components/ui/kv-amount.vue";
import FormatMixin from "../mixins/format-mixin.js";

export default {
    name: "BalanceSummary",
    mixins: [FormatMixin],
    components: {
        KvPopup,
        KvButton,
        ModalRechargeHistory,
        ModalAdjustMonthly,
        ModalExtraPackage,
        HumanDate,
        KvSkeleton,
        KvAmount
    },
    props: {
        balance: { type: Number, default: 0 },
        credits: { type: Array, default: () => [] },
        creditsPerMonth: { type: Number, default: 0 },
        priceMonthlyCredits: { type: Number, default: 0 },
        placeCurrentCreditsPerMonth: { type: Number, default: 0 },
        plan: { type: Object, default: () => ({}) },
        automationsUsage: { type: Object, default: () => ({ automations: [], campaigns: [] }) },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            showFuturePackages: false
        }
    },
    computed: {
        ...mapState(['user', 'place']),
        activeCreditPackages() {
            const now = new Date();
            return (this.credits || [])
                .filter(c => c && new Date(c.expiresAt) > now && parseFloat(c.available) > 0)
                .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
                .map(c => {
                    const amount = parseFloat(c.amount || 0);
                    const available = parseFloat(c.available || 0);
                    const used = Math.max(0, amount - available);
                    const percentage = amount > 0 ? (available / amount) * 100 : 0;
                    
                    let label = this.t('balance-credit_package');
                    if (c.type_of_credit === 'monthly') label = this.t('balance-monthly_package_label');
                    else if (c.type_of_credit === 'single') label = this.t('balance-single_package_label');
                    else if (c.type_of_credit === 'monthly-partial') label = this.t('balance-monthly_partial_label');
                    
                    return {
                        ...c,
                        label,
                        amount,
                        available,
                        used,
                        consumedPercentage: (100 - percentage),
                        usage_percent: percentage,
                        percentage: Math.min(percentage, 100)
                    };
                });
        },
        consumptionStatus() {
            if (!this.currentActivePackage) return 'success';
            const consumed = this.currentActivePackage.consumedPercentage;
            if (consumed >= 90) return 'danger';
            if (consumed >= 75) return 'warning';
            return 'success';
        },
        consumptionStatusClass() {
            return this.getPackageStatusClass(this.currentActivePackage);
        },
        currentActivePackage() {
            return this.activeCreditPackages.length ? this.activeCreditPackages[0] : null;
        },
        futureActivePackages() {
            return this.activeCreditPackages.slice(1);
        },
        estimatedUsageEndOfMonth() {
            const today = moment();
            const endOfMonth = moment().endOf('month');
            const daysUntilEndOfMonth = endOfMonth.diff(today, 'days');
            
            let needed = 0;
            const automations = this.automationsUsage && this.automationsUsage.automations;
            if (Array.isArray(automations)) {
                automations.forEach((item) => {
                    needed += (parseFloat(item.dailyUsage) || 0) * daysUntilEndOfMonth;
                });
            }

            const campaigns = this.automationsUsage && this.automationsUsage.campaigns;
            if (Array.isArray(campaigns)) {
                campaigns.forEach((item) => {
                    const campaignDate = moment(item.date);
                    if (campaignDate.isSameOrBefore(endOfMonth)) {
                        needed += parseFloat(item.estimatedCredits) || 0;
                    }
                });
            }
            return this.formatNumber(Math.abs(needed), 2);
        }
    },
    methods: {
        getPackageStatusClass(pkg) {
            if (!pkg) return 'bg-success';
            return this.getStatusClassByPercent(pkg.consumedPercentage);
        },
        getStatusClassByPercent(percent) {
            if (percent >= 90) return 'bg-danger';
            if (percent >= 75) return 'bg-warning';
            return 'bg-success';
        }
    }
}
</script>

<style scoped lang="scss">
.gap-xs { gap: 4px; }
.gap-sm { gap: 8px; }
.gap-md { gap: 16px; }

.font-size-sm { font-size: 13px; }
.letter-spacing-sm { letter-spacing: 0.02em; }

.v-divider { height: 32px; }

.status-container {
    width: 100%;
    position: relative;
}

.credits-alert {
    font-size: 14px;
}

.package-label {
    color: var(--gray-700, #374151);
    font-size: 15px;

    @media (max-width: 576px) {
        font-size: 14px;
    }
}

.package-expiry {
    font-size: 13px;

    @media (max-width: 576px) {
        font-size: 12px;
    }
}

.progress-main {
    height: 24px;
    border-radius: 12px;
    background-color: var(--gray-100, #F1F5F9);
    cursor: default;
    overflow: hidden;
    display: flex;

    @media (max-width: 576px) {
        height: 20px;
    }

    .bg-success {
        background-color: #2AAA4C !important;
    }
    
    .bg-warning {
        background-color: #EAB308 !important;
    }
    
    .bg-danger {
        background-color: #EF4444 !important;
    }
}

.progress-compact {
    height: 8px;
    border-radius: 4px;
    background-color: var(--gray-100, #F1F5F9);
    overflow: hidden;
    display: flex;

    .bg-success {
        background-color: #2AAA4C !important;
    }
    
    .bg-warning {
        background-color: #EAB308 !important;
    }
    
    .bg-danger {
        background-color: #EF4444 !important;
    }
}

.usage-fraction {
    font-family: "Sora", sans-serif;
    font-size: 14px;

    @media (max-width: 576px) {
        font-size: 12px;
    }
}

.usage-percent {
    color: var(--gray-900, #111827);
}

.usage-detail {
    color: var(--gray-400, #9CA3AF);
    margin-left: 4px;
}

.usage-estimate {
    font-size: 13px;
    color: var(--gray-500, #6B7280);

    @media (max-width: 576px) {
        font-size: 12px;
    }
}

.usage-estimate-value {
    color: var(--gray-700, #374151);
}

.empty-state-text {
    font-size: 14px;
}

.balance-clickable {
    font-family: "Sora", sans-serif;
    cursor: pointer;
    color: white;
    padding: 2px 12px;
    border-radius: 8px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 1.4em;
    font-weight: 500;
    line-height: 1.2;

    @media (max-width: 576px) {
        font-size: 1.25em;
        padding: 2px 10px;
    }

    &.bg-success {
        background-color: #2AAA4C;
        &:hover {
            background-color: #228b3e;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(42, 170, 76, 0.25);
        }
    }

    &.bg-warning {
        background-color: #EAB308;
        &:hover {
            background-color: #ca9b06;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(234, 179, 8, 0.25);
        }
    }

    &.bg-danger {
        background-color: #EF4444;
        &:hover {
            background-color: #dc2626;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
        }
    }
}

.btn-manage {
    color: var(--gray-400, #9CA3AF) !important;
    &:hover {
        color: var(--gray-600, #4B5563) !important;
        background-color: var(--gray-100, #F1F5F9) !important;
    }
}

.kv-popup-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
}
.kv-popup-title {
    font-size: 13px;
    font-weight: 500;
    color: #6C737F;
    padding: 8px 16px 4px;
    display: block;
}

.progress-hover-container {
    position: relative;
    padding: 10px;
    margin: -10px;
    border-radius: 12px;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: #F8FAFC;
    }

    .future-packages-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: var(--white, #FFFFFF);
        border: 1px solid var(--gray-200, #E5E7EB);
        border-radius: 12px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        padding: 12px;
        z-index: 50;
        display: flex;
        flex-direction: column;
        gap: 12px;

        @media (max-width: 768px) {
            position: fixed;
            top: auto;
            bottom: 20px;
            left: 20px;
            right: 20px;
            width: auto;
        }

        .future-dropdown-header {
            font-size: 12px;
            text-transform: uppercase;
            font-weight: 500;
            color: var(--gray-400, #9CA3AF);
            letter-spacing: 0.05em;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid var(--gray-100, #F1F5F9);
        }

        .future-pkg-item {
            padding: 12px 12px;
            border-bottom: 1px solid #F8FAFC;
            
            &:last-child {
                border-bottom: none;
            }

            .future-pkg-label {
                font-size: 13px;
                font-weight: 500;
                color: #374151;
            }
            .future-pkg-expires {
                font-size: 12px;
                color: #9CA3AF;
            }
            .future-pkg-details {
                font-family: "Sora", sans-serif;
                font-size: 12px;
                color: #6B7280;
                margin-top: 6px;
                display: block;
            }
        }

        .future-packages-scrollable {
            max-height: 280px;
            overflow-y: auto;
            margin: 0 -12px;
            
            &::-webkit-scrollbar {
                width: 4px;
            }
            &::-webkit-scrollbar-track {
                background: #F9FAFB;
            }
            &::-webkit-scrollbar-thumb {
                background: #E5E7EB;
                border-radius: 4px;
            }
        }

        .future-dropdown-footer {
            margin-top: 4px;
            padding-top: 12px;
            border-top: 1px solid #F1F5F9;
            text-align: center;
            
            a, button {
                font-size: 12px;
                color: #2AAA4C;
                font-weight: 500;
                text-decoration: none;
                transition: color 0.2s;
                
                &:hover {
                    color: #1f7d37;
                    text-decoration: underline;
                }
            }
        }
    }
}

.section-balance {
    .d-flex.flex-row.align-items-center.justify-content-between {
        @media (max-width: 576px) {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px;

            .d-flex.align-items-center.gap-sm {
                width: 100%;
                justify-content: space-between;
            }
        }
    }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s, transform 0.2s;
    transform-origin: top center;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

.progress-bar-interactive {
    transition: filter 0.2s, opacity 0.2s;
    cursor: default;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    
    &:last-child {
        border-right: none;
    }

    &:hover {
        filter: brightness(1.15);
        opacity: 0.95;
    }
}

</style>
